import React from "react";

import { doNothing } from "@examspark/common";
import {
  ExamBlobs,
  ExamItemResponses,
  Resource,
  SerializedHighlight,
} from "@examspark/types";

import { ItemPosition } from "../../../utils/sections";

export interface Accessibility {
  fontSize?: FontSize;
}

export type FontSize = "small" | "normal" | "large" | "xlarge" | "huge";

export interface ExamState {
  count: number;
  isCompleted?: boolean;
  isPaused?: boolean;
  /** Modal to show that saves and allows user to exit. */
  isPausedWithExitPrompt?: boolean;
  flaggedItemIds: string[];
  position: ItemPosition;
  windowSize?: DOMRectReadOnly;
  accessibility?: Accessibility;
  /** Contains exam and question data used to effectively play the exam */
  exam?: Resource;
  responses?: ExamItemResponses;

  blobs?: ExamBlobs;

  highlights?: {
    [placementId: string]: SerializedHighlight[];
  };

  // if null, dont shuffle
  shuffleSeed?: string | null;
  // Controls how you can navigate the exam sections
  // Default is "non-linear" (aka, free navigation)
  itemNavigationMode: "linear" | "non-linear";
  sectionNavigationMode: "linear" | "non-linear";
}

export const defaultState: ExamState = {
  count: 1,
  isPaused: false,
  isCompleted: false,
  accessibility: {
    fontSize: "normal",
  },
  flaggedItemIds: [],
  position: {
    form: 0,
    section: 0,
    question: 0,
  },
  itemNavigationMode: "non-linear",
  sectionNavigationMode: "non-linear",
  responses: {},
  blobs: {},
};

export const ExamContext = React.createContext<
  [ExamState, React.Dispatch<ExamReducerActions>]
>([defaultState, doNothing]);

const fontSizes: FontSize[] = ["normal", "large", "xlarge", "huge"];

export type ExamReducerActions =
  | {
      type: "NEXT";
    }
  | {
      type: "BACK";
    }
  | {
      type: "TOGGLE_FLAG";
      [key: string]: any;
    }
  | {
      type: "SET_ITEM_RESPONSE";
      [key: string]: any;
    }
  | {
      type: "CLEAR_ITEM_RESPONSE";
      [key: string]: any;
    }
  | {
      type: "SET_ITEM_BLOB";
      data: {
        // this ID is the questionId
        id: string;
        blobs: ExamBlobs[string];
      };
    }
  | {
      type: "SET_ITEM_HIGHLIGHTS";
      data: {
        placementId: string;
        highlights: SerializedHighlight[];
      };
    }
  | {
      type: "INCREASE_FONT";
      [key: string]: any;
    }
  | {
      type: "DECREASE_FONT";
      [key: string]: any;
    }
  | {
      type: "SET_WINDOW_SIZE";
      [key: string]: any;
    }
  | {
      type: "SET_PAUSE";
      [key: string]: any;
    }
  | {
      type: "SET_PAUSED_WITH_EXIT_PROMPT";
      value: boolean;
    }
  | {
      type: "COMPLETE";
    }
  | {
      type: "TOGGLE_PAUSE";
    }
  | {
      type: "SET_POSITION";
      [key: string]: any;
    }
  | {
      type: "SET_EXAM";
      data: any;
    }
  | {
      type: "RESET";
    };

export const reducer = (state: ExamState, action: ExamReducerActions) => {
  const getItemId = () => {
    return (
      state.exam?.content?.forms?.[state.position.form]?.sections?.[
        state.position.section
      ]?.questionIds?.[state.position.question] ?? undefined
    );
  };

  const getSectionQuestions = (form: number, section: number) => {
    if (!state.exam) return [];
    return (
      state.exam?.content?.forms?.[form]?.sections?.[section]?.questionIds || []
    );
  };

  const getNextPosition = () => {
    const sectionQuestions = getSectionQuestions(
      state.position.form,
      state.position.section,
    );

    const isLastQuestion =
      state.position.question === sectionQuestions.length - 1;

    const nextPosition = {
      ...state.position,
      section: isLastQuestion
        ? state.position.section + 1
        : state.position.section,
      question: isLastQuestion ? 0 : state.position.question + 1,
    };

    state.position = nextPosition;
  };

  const getPreviousPosition = () => {
    const previousSectionQuestions = getSectionQuestions(
      state.position.form,
      state.position.section - 1,
    );

    const isFirstQuestion = state.position.question === 0;
    const previousSectionLastQuestion = previousSectionQuestions.length - 1;

    const previousPosition = {
      ...state.position,
      section: isFirstQuestion
        ? state.position.section - 1
        : state.position.section,
      question: isFirstQuestion
        ? previousSectionLastQuestion
        : state.position.question - 1,
    };

    console.log({
      sectionQuestions: previousSectionQuestions,
      isFirstQuestion,
      previousSectionLastQuestion,
      previousPosition,
    });

    state.position = previousPosition;
  };

  const getIncreasedFontSize = (): FontSize => {
    if (!state.accessibility?.fontSize) {
      return "large";
    }
    const currentIndex = fontSizes.findIndex(
      (s) => s === state.accessibility?.fontSize,
    );

    if (currentIndex === -1) {
      return "large";
    }

    if (!fontSizes[currentIndex + 1]) {
      return fontSizes[currentIndex];
    }

    return fontSizes[currentIndex + 1];
  };

  const getDecreasedFontSize = (): FontSize => {
    if (!state.accessibility?.fontSize) {
      return "normal";
    }
    const currentIndex = fontSizes.findIndex(
      (s) => s === state.accessibility?.fontSize,
    );

    if (currentIndex === -1) {
      return "normal";
    }

    if (!fontSizes[currentIndex - 1]) {
      return fontSizes[currentIndex];
    }

    return fontSizes[currentIndex - 1];
  };

  switch (action.type) {
    case "NEXT":
      getNextPosition();
      break;
    case "BACK":
      getPreviousPosition();
      break;
    case "TOGGLE_FLAG": {
      const itemId =
        state.exam?.content?.forms?.[state.position.form]?.sections?.[
          state.position.section
        ]?.questionIds?.[state.position.question];
      if (itemId) {
        const toggleFlagIndex = state.flaggedItemIds.findIndex((_itemId) => {
          return _itemId === itemId;
        });
        if (toggleFlagIndex !== -1) {
          state.flaggedItemIds.splice(toggleFlagIndex, 1);
        } else {
          state.flaggedItemIds.push(itemId);
        }
      }
      break;
    }
    case "SET_ITEM_RESPONSE":
      if (!state.responses) state.responses = {};
      state.responses[action.itemId] = action.response;
      break;
    case "CLEAR_ITEM_RESPONSE": {
      const clearResponseItemId = getItemId();
      if (state.responses && clearResponseItemId) {
        console.log("clearing", clearResponseItemId, state.responses);
        state.responses[clearResponseItemId] = {};
      }
      break;
    }
    case "SET_ITEM_BLOB":
      if (!state.blobs) state.blobs = {};
      if (action.data && action.data.id) {
        state.blobs[action.data.id] = action.data.blobs;
      }
      break;

    case "SET_ITEM_HIGHLIGHTS": {
      if (!state.highlights) state.highlights = {};
      if (action.data && action.data.placementId) {
        state.highlights[action.data.placementId] = action.data.highlights;
      }

      break;
    }
    case "INCREASE_FONT":
      if (!state.accessibility) {
        state.accessibility = {};
      }
      state.accessibility.fontSize = getIncreasedFontSize();
      break;
    case "DECREASE_FONT":
      if (!state.accessibility) {
        state.accessibility = {};
      }
      state.accessibility.fontSize = getDecreasedFontSize();
      break;
    case "SET_PAUSE":
      state.isPaused = action.isPaused;
      break;
    case "SET_PAUSED_WITH_EXIT_PROMPT":
      state.isPausedWithExitPrompt = action.value;
      break;
    case "SET_WINDOW_SIZE":
      state.windowSize = action.windowSize;
      break;
    case "TOGGLE_PAUSE":
      state.isPaused = !state.isPaused;
      break;
    case "RESET":
      state = Object.assign(state, defaultState);
      break;
    case "COMPLETE":
      state.isCompleted = true;
      break;
    case "SET_POSITION":
      state.position = action.position;
      break;
    case "SET_EXAM":
      return {
        ...defaultState,
        ...state,
        exam: action.data,
      };
  }
};
